import React, {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ImagePickerConf } from "./index.models";
import labels from "./i18n/es.json";
import { Button, Image as ImageComponent, Skeleton, Space, Dropdown } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  UploadOutlined,
  CameraOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import ParentSize from "../ParentSize";
import type { MenuProps } from "antd";
import { useClientContext } from "@data-root/react";

export * from "./index.models";

interface ImagePickerProps {
  config: ImagePickerConf;
  imageSrc?: string;
  imageChanged?: (uri: string | null) => void;
  editable?: boolean;
}

const initialConfig: ImagePickerConf = {
  language: "en",
  objectFit: "contain",
  hideDeleteBtn: false,
  hideDownloadBtn: false,
  hideAddBtn: false,
  compressInitial: null,
};

const compress = (url: string, ratio: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = document.createElement("img");
    image.src = url;

    image.onerror = reject;

    image.onload = () => {
      const canvas = new OffscreenCanvas(image.width, image.height);
      const ctx = canvas.getContext("2d");

      if (!ctx) return reject(new Error("Canvas context not supported"));

      const width = image.width;
      const height = image.height;

      canvas.width = width;
      canvas.height = height;

      ctx.drawImage(image, 0, 0, width, height);

      canvas
        .convertToBlob({ type: "image/jpeg", quality: ratio })
        .then((blob) => resolve(URL.createObjectURL(blob)), reject);
    };
  });
};

const ImagePicker: FC<ImagePickerProps> = memo(
  ({ config = {}, imageSrc = null, imageChanged, editable = true }) => {
    const { desktop: isDesktop } = useClientContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(imageSrc ? true : false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [useCamera, setUseCamera] = useState(false);
    const [shouldTriggerInput, setShouldTriggerInput] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const fileTypeRef = useRef("");
    const fileNameRef = useRef("download");

    const uuid = useMemo(() => Date.now().toString(20), []);

    const configuration = useMemo(
      () => ({
        ...initialConfig,
        ...config,
      }),
      [config]
    );

    const imageComponentStyle = useMemo(
      () => ({
        height: 200,
        objectFit: configuration.objectFit,
      }),
      [configuration.objectFit]
    );

    const onChange = useCallback(
      async (event: ChangeEvent<HTMLInputElement>) => {
        console.log("onChange");

        const files = event.target?.files;
        if (!files) return;

        const file = files[0];
        if (!file) return;
        if (!file.type.includes("image")) return;

        fileNameRef.current = file.name.split(".")[0];
        fileTypeRef.current = file.type;

        console.time("onChange");

        setLoading(true);

        const url = await compress(URL.createObjectURL(file), 0.5);

        setLoaded(true);
        setLoading(false);

        if (imageChanged) imageChanged(url);

        console.timeEnd("onChange");
      },
      [imageChanged]
    );

    const onUpload = useCallback((event: any) => {
      console.log("onUpload");
      console.time("onUpload");

      event.preventDefault();
      inputRef?.current?.click();
      console.timeEnd("onUpload");
    }, []);

    const onRemove = useCallback(() => {
      setLoaded(false);

      if (inputRef.current) inputRef.current.value = null;
      if (imageChanged) imageChanged(null);
    }, [imageChanged]);

    const handleMenuClick = useCallback(({ key }: { key: string }) => {
      setUseCamera(isDesktop ? false : key === "camera");
      setShouldTriggerInput(true);
    }, [isDesktop]);

    useEffect(() => {
      if (shouldTriggerInput) {
        inputRef?.current?.click();
        setShouldTriggerInput(false);
      }
    }, [shouldTriggerInput]);

    const items: MenuProps["items"] = isDesktop ? [
      {
        key: "gallery",
        icon: <PictureOutlined />,
        label: "Subir desde galería",
      },
    ] : [
      {
        key: "camera",
        icon: <CameraOutlined />,
        label: "Usar cámara",
      },
      {
        key: "gallery",
        icon: <PictureOutlined />,
        label: "Subir desde galería",
      },
    ];

    useEffect(() => {
      if (imageSrc !== "" && imageSrc !== null) setLoaded(true);
    }, [imageSrc]);
    
    return (
      <React.Fragment>
        <input
          ref={inputRef}
          id={`filePicker-${uuid}`}
          type="file"
          accept="image/*"
          capture={useCamera ? "environment" : undefined}
          style={inputStyle}
          onChange={onChange}
        />

        {loading ? (
          <Space direction="vertical" style={style100}>
            <div style={divStyle}>
              <ParentSize>
                {({ width }) => (
                  <Skeleton.Image
                    active
                    style={{
                      width: width,
                      height: 200,
                    }}
                  />
                )}
              </ParentSize>
            </div>

            {editable && (
              <Dropdown
                menu={{ items, onClick: handleMenuClick }}
                placement="bottomCenter"
              >
                <Button icon={<UploadOutlined />}>
                  {labels["Upload a image"]}
                </Button>
              </Dropdown>
            )}
          </Space>
        ) : loaded ? (
          <Space direction="vertical" style={style100} key={imageSrc + "image"}>
            <div
              style={{
                width: "100%",
                height: 200,
              }}
            >
              <ParentSize>
                {({ width }) => (
                  <ImageComponent
                    src={imageSrc as string}
                    style={imageComponentStyle}
                    width={width}
                  />
                )}
              </ParentSize>
            </div>

            {editable && (
              <Space wrap style={style100}>
                {!configuration.hideAddBtn && (
                  <Dropdown
                    menu={{ items, onClick: handleMenuClick }}
                    placement="bottomCenter"
                  >
                    <Button icon={<UploadOutlined />}>
                      {labels["Upload a image"]}
                    </Button>
                  </Dropdown>
                )}

                {!configuration.hideDownloadBtn && (
                  <Button
                    icon={<DownloadOutlined />}
                    href={imageSrc as string}
                    download={fileNameRef.current}
                  >
                    {labels["Download the image"]}
                  </Button>
                )}

                {!configuration.hideDeleteBtn && (
                  <Button icon={<DeleteOutlined />} onClick={onRemove}>
                    {labels["Remove"]}
                  </Button>
                )}
              </Space>
            )}
          </Space>
        ) : (
          <Space direction="vertical" style={style100}>
            <div style={divStyle}>
              <ParentSize>
                {({ width }) => (
                  <Skeleton.Image
                    style={{
                      width: width,
                      height: 200,
                    }}
                  />
                )}
              </ParentSize>
            </div>

            {editable && (
              isDesktop ? (
                <Button icon={<UploadOutlined />} onClick={onUpload}>
                  {labels["Upload a image"]}
                </Button>
              ) : (
                <Dropdown
                  menu={{ items, onClick: handleMenuClick }}
                  placement="bottomCenter"
                >
                  <Button icon={<UploadOutlined />}>
                    {labels["Upload a image"]}
                  </Button>
                </Dropdown>
              )
            )}
          </Space>
        )}
      </React.Fragment>
    );
  }
);

const divStyle = {
  width: "100%",
  height: 200,
};

const inputStyle = {
  display: "none",
};

const style100 = {
  width: "100%",
};

export default ImagePicker;
